import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Stack,
  Group,
  Button,
  Table,
  ScrollArea,
  Modal,
  TextInput,
  Textarea,
  Grid,
  Text,
  Alert,
  Center,
  Loader
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconEdit, IconTrash } from '@tabler/icons-react';
import { API_URL } from '../../utils/constants';
import '../admin/AccountStyles.css';

function SetupBarHeadersScreen() {
  // BAR Header state
  const [barHeaders, setBarHeaders] = useState([]);
  const [loadingBarHeaders, setLoadingBarHeaders] = useState(true);
  const [savingBarHeader, setSavingBarHeader] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBarHeader, setSelectedBarHeader] = useState(null);
  
  // BAR Header Modal state
  const [showBarHeaderModal, setShowBarHeaderModal] = useState(false);
  const [barHeaderModalMode, setBarHeaderModalMode] = useState('add'); // 'add' or 'edit'
  const [barHeaderForm, setBarHeaderForm] = useState({ bar: '', description: '' });
  const [barHeaderErrors, setBarHeaderErrors] = useState({});

  // Error and success messages
  const [message, setMessage] = useState({ text: '', variant: '', show: false });

  useEffect(() => {
    const checkBackend = async () => {
      try {
        const response = await fetch(`${API_URL}/health`);
        if (!response.ok) {
          setMessage({ 
            text: 'Backend server is not accessible. Please ensure the backend is running.', 
            variant: 'danger', 
            show: true 
          });
        }
      } catch (error) {
        setMessage({ 
          text: 'Backend server is not accessible. Please ensure the backend is running.', 
          variant: 'danger', 
          show: true 
        });
      }
    };
    
    checkBackend();
    fetchBarHeaders();
  }, []);

  // API Functions for BAR Headers
  const fetchBarHeaders = async () => {
    try {
      setLoadingBarHeaders(true);
      const response = await fetch(`${API_URL}/rates-bar-header`);
      if (response.ok) {
        const data = await response.json();
        setBarHeaders(data);
      } else {
        throw new Error('Failed to fetch BAR headers');
      }
    } catch (error) {
      console.error('Error fetching BAR headers:', error);
      setMessage({ text: 'Failed to load BAR headers', variant: 'danger', show: true });
    } finally {
      setLoadingBarHeaders(false);
    }
  };

  const createBarHeader = async (barHeaderData) => {
    try {
      const response = await fetch(`${API_URL}/rates-bar-header`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(barHeaderData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create BAR header');
      }

      return response.json();
    } catch (error) {
      console.error('Error creating BAR header:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
      throw error;
    }
  };

  const updateBarHeader = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/rates-bar-header/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update BAR header');
      }

      return response.json();
    } catch (error) {
      console.error('Error updating BAR header:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
      throw error;
    }
  };

  const deleteBarHeader = async (id) => {
    if (!window.confirm('Are you sure you want to delete this BAR header? This will also delete all associated rates.')) return;
    
    try {
      const response = await fetch(`${API_URL}/rates-bar-header/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete BAR header');
      }

      setBarHeaders(barHeaders.filter(header => header.id !== id));
      setMessage({ text: 'BAR header deleted successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error deleting BAR header:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  // BAR Header Modal handlers
  const handleAddBarHeader = () => {
    setBarHeaderModalMode('add');
    setSelectedBarHeader(null);
    setBarHeaderForm({ bar: '', description: '' });
    setBarHeaderErrors({});
    setShowBarHeaderModal(true);
  };

  const handleEditBarHeader = (barHeader) => {
    setBarHeaderModalMode('edit');
    setSelectedBarHeader(barHeader);
    setBarHeaderForm({ 
      bar: barHeader.bar, 
      description: barHeader.description || '' 
    });
    setBarHeaderErrors({});
    setShowBarHeaderModal(true);
  };

  const handleDeleteBarHeader = async (barHeaderId) => {
    if (!window.confirm('Are you sure you want to delete this BAR header? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteBarHeader(barHeaderId);
      await fetchBarHeaders();
    } catch (error) {
      console.error('Error deleting BAR header:', error);
      setMessage({ text: 'Failed to delete BAR header', variant: 'danger', show: true });
    }
  };

  const handleCloseBarHeaderModal = () => {
    setShowBarHeaderModal(false);
    setSelectedBarHeader(null);
    setBarHeaderForm({ bar: '', description: '' });
    setBarHeaderErrors({});
  };

  const handleBarHeaderInputChange = (field, value) => {
    setBarHeaderForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (barHeaderErrors[field]) {
      setBarHeaderErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSaveBarHeader = async () => {
    // Validation
    const errors = {};
    if (!barHeaderForm.bar.trim()) {
      errors.bar = 'BAR name is required';
    }
    
    if (Object.keys(errors).length > 0) {
      setBarHeaderErrors(errors);
      return;
    }

    setSavingBarHeader(true);
    try {
      if (barHeaderModalMode === 'add') {
        await createBarHeader(barHeaderForm);
      } else {
        await updateBarHeader(selectedBarHeader.id, barHeaderForm);
      }
      
      setMessage({ text: 'BAR header saved successfully!', variant: 'success', show: true });
      handleCloseBarHeaderModal();
      await fetchBarHeaders();
    } catch (error) {
      // Error already handled in the API functions
    } finally {
      setSavingBarHeader(false);
    }
  };

  // Filter BAR headers based on search term
  const filteredBarHeaders = barHeaders.filter(header => {
    const bar = header.bar || '';
    const description = header.description || '';
    const searchLower = searchTerm.toLowerCase();
    
    return bar.toLowerCase().includes(searchLower) ||
           description.toLowerCase().includes(searchLower);
  });

  // Handle row selection
  const handleRowSelect = (header) => {
    setSelectedBarHeader(header);
  };

  return (
    <Container fluid className="account-screen p-0">
      <div className="d-flex">
        {/* Side Menu */}
        <div className="side-menu-container" style={{ width: '250px', minHeight: '100vh' }}>
          <div className="side-menu">
            <div className="side-menu-section">
              <div style={{ padding: '1.5rem', borderBottom: '2px solid #dee2e6', backgroundColor: '#34495e' }}>
                <h3 style={{ color: '#ecf0f1', fontWeight: 700, margin: 0, fontSize: '1.3rem' }}>BAR Headers</h3>
              </div>
              <div style={{ padding: '1rem 0' }}>
                <Button 
                  variant="outline-light" 
                  className="side-menu-btn w-100 mb-2"
                  onClick={() => setSearchTerm('')}
                >
                  Find
                </Button>
                <Button 
                  variant="outline-light" 
                  className="side-menu-btn w-100 mb-2"
                  onClick={handleAddBarHeader}
                >
                  Add
                </Button>
                <Button 
                  variant="outline-light" 
                  className="side-menu-btn w-100 mb-2"
                  onClick={() => selectedBarHeader && handleEditBarHeader(selectedBarHeader)}
                  disabled={!selectedBarHeader}
                >
                  Change
                </Button>
                <Button 
                  variant="outline-light" 
                  className="side-menu-btn w-100 mb-2"
                  onClick={() => selectedBarHeader && handleDeleteBarHeader(selectedBarHeader.id)}
                  disabled={!selectedBarHeader}
                >
                  Delete
                </Button>
                
                {/* Line break */}
                <hr style={{ borderColor: '#bdc3c7', margin: '1rem 0' }} />
                
                {/* Import/Export buttons */}
                <Button 
                  variant="outline-light" 
                  className="side-menu-btn w-100 mb-2"
                  onClick={() => console.log('Import clicked')}
                >
                  Import
                </Button>
                <Button 
                  variant="outline-light" 
                  className="side-menu-btn w-100 mb-2"
                  onClick={() => console.log('Export clicked')}
                >
                  Export
                </Button>
                <Button 
                  variant="outline-light" 
                  className="side-menu-btn w-100 mb-2"
                  onClick={() => console.log('Backup clicked')}
                >
                  Backup
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content" style={{ flex: 1, padding: '2rem' }}>
          <Stack gap="md">
            <Group justify="space-between">
              <Title order={2}>BAR Headers</Title>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={handleAddBarHeader}
              >
                Add BAR Header
              </Button>
            </Group>

            {message.show && (
              <Alert color={message.variant === 'danger' ? 'red' : message.variant === 'success' ? 'green' : 'blue'}>
                {message.text}
              </Alert>
            )}

            <Paper p="md">
              <Stack gap="md">
                <Group justify="space-between">
                  <TextInput
                    placeholder="Search BAR headers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: 300 }}
                  />
                </Group>
                
                {loadingBarHeaders ? (
                  <Center py="xl">
                    <Loader size="md" />
                    <Text ml="md">Loading BAR headers...</Text>
                  </Center>
                ) : filteredBarHeaders.length === 0 ? (
                  <Alert color="orange" title="No BAR Headers Found">
                    {searchTerm ? 'No BAR headers match your search criteria.' : 'No BAR headers have been created yet.'}
                  </Alert>
                ) : (
                  <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>BAR Name</Table.Th>
                          <Table.Th>Description</Table.Th>
                          <Table.Th>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {filteredBarHeaders.map(header => (
                          <Table.Tr 
                            key={header.id}
                            style={{ 
                              backgroundColor: selectedBarHeader?.id === header.id ? '#e3f2fd' : 'transparent',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleRowSelect(header)}
                          >
                            <Table.Td>
                              <Text fw={500}>{header.bar}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" c="dimmed">{header.description || 'No description'}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Group gap="xs">
                                <Button
                                  variant="subtle"
                                  size="sm"
                                  color="blue"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditBarHeader(header);
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="subtle"
                                  size="sm"
                                  color="red"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteBarHeader(header.id);
                                  }}
                                >
                                  Delete
                                </Button>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </div>
                )}
              </Stack>
            </Paper>
          </Stack>
        </div>
      </div>

      {/* BAR Header Modal */}
      <Modal 
        opened={showBarHeaderModal} 
        onClose={handleCloseBarHeaderModal}
        size="lg"
        title={barHeaderModalMode === 'add' ? 'Add BAR Header' : 'Edit BAR Header'}
      >
        <Stack gap="md">
          <TextInput
            label="BAR Name"
            placeholder="e.g., Standard Rate, Weekend Rate, Holiday Rate"
            value={barHeaderForm.bar}
            onChange={(e) => handleBarHeaderInputChange('bar', e.target.value)}
            error={barHeaderErrors.bar}
            required
          />

          <Textarea
            label="Description"
            placeholder="Optional description for this rate category"
            value={barHeaderForm.description}
            onChange={(e) => handleBarHeaderInputChange('description', e.target.value)}
            rows={3}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={handleCloseBarHeaderModal}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveBarHeader}
              loading={savingBarHeader}
            >
              {savingBarHeader ? 'Saving...' : 'Save'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default SetupBarHeadersScreen;
